import { useRef, useState } from "react";
import dayjs from "dayjs";
import { Sheet } from "./ui";

/** "Add from card": take a photo of a business card, read it on the phone, then review in the normal Add person form. */
export function CardScanButton({ onRead }: { onRead: (initial: Record<string, string>, photo: { url: string; text: string }) => void }) {
  const input = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [prog, setProg] = useState<{ p: number; label: string } | null>(null);
  const [err, setErr] = useState("");
  const [rotate, setRotate] = useState(0);

  async function read(f: File, rot = 0) {
    setFile(f); setErr(""); setRotate(rot); setProg({ p: 0, label: "Getting the reader ready…" });
    try {
      const { readCard, parseCard } = await import("../lib/cardscan");
      const text = await readCard(f, (p, label) => setProg({ p, label }), rot);
      const c = parseCard(text);
      if (!c.first_name && !c.email && !c.phone) { setProg(null); setErr("Couldn't make out the card. Try again in good light, with the card filling the photo, or turn it."); return; }
      const notes = [c.website && `Website: ${c.website}`, c.other_phones?.length && `Other numbers: ${c.other_phones.join(", ")}`].filter(Boolean).join(" · ");
      const init: Record<string, string> = {
        first_name: c.first_name ?? "", last_name: c.last_name ?? "", phone: c.phone ?? "", email: c.email ?? "",
        company: c.company ?? "", occupation: c.occupation ?? "", notes, source: "Business card", date_met: dayjs().format("YYYY-MM-DD"),
      };
      setProg(null); setFile(null);
      onRead(init, { url: URL.createObjectURL(f), text });
    } catch (e) {
      setProg(null);
      setErr(`The card reader didn't load (${(e as Error).message}). Check your connection and try again.`);
    }
  }

  return (
    <>
      <button className="btn" onClick={() => input.current?.click()}>Add from card</button>
      <input ref={input} type="file" accept="image/*" capture="environment" hidden
        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) read(f); }} />
      {(prog || err) && (
        <Sheet title="Reading the card" onClose={() => { setProg(null); setErr(""); setFile(null); }}>
          {prog && <div className="nl-progress" aria-live="polite">
            <div className="bar"><span style={{ width: `${Math.round(prog.p * 100)}%` }} /></div>
            <div className="small muted">{prog.label}</div>
          </div>}
          {err && <>
            <div className="error-box" role="alert">{err}</div>
            <div className="toolbar" style={{ marginTop: 12 }}>
              {file && <button className="btn" onClick={() => read(file, (rotate + 90) % 360)}>Turn it and read again</button>}
              <button className="btn primary" onClick={() => { setErr(""); input.current?.click(); }}>Take another photo</button>
            </div>
          </>}
          <p className="small muted">The card is read on this device. The photo isn't uploaded anywhere.</p>
        </Sheet>
      )}
    </>
  );
}
